import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faPuzzlePiece, faBullhorn, faUsers, faChartLine,
  faExclamationTriangle, faCheckCircle, faClock,
} from '@fortawesome/free-solid-svg-icons';

import {
  useGetSafetyLogsQuery,
  useGetOccurrencesQuery,
  useGetArticlesQuery,
  useGetUsersQuery,
} from '../../redux/apiSlice';

function Dashboard() {
  // Live data hooks
  const { data: safetyLogs } = useGetSafetyLogsQuery();
  const { data: occurrences } = useGetOccurrencesQuery();
  const { data: articles } = useGetArticlesQuery();
  const { data: usersResult } = useGetUsersQuery('');

  // Helpers for MoM calculations
  const countInMonth = (items, getDate) => {
    if (!Array.isArray(items)) return 0;
    const now = new Date();
    const m = now.getMonth();
    const y = now.getFullYear();
    return items.filter((it) => {
      const raw = getDate(it);
      if (!raw) return false;
      const d = new Date(raw);
      return !Number.isNaN(d) && d.getMonth() === m && d.getFullYear() === y;
    }).length;
  };
  const countInPrevMonth = (items, getDate) => {
    if (!Array.isArray(items)) return 0;
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const pm = prev.getMonth();
    const py = prev.getFullYear();
    return items.filter((it) => {
      const raw = getDate(it);
      if (!raw) return false;
      const d = new Date(raw);
      return !Number.isNaN(d) && d.getMonth() === pm && d.getFullYear() === py;
    }).length;
  };
  const pctChangeMoM = (current, previous) => {
    if (!previous) return current ? '+100%' : '0%';
    const pct = Math.round(((current - previous) / previous) * 100);
    return `${pct >= 0 ? '+' : ''}${pct}%`;
  };

  // Safety logs
  const safetyLogsThisMonth = countInMonth(safetyLogs, (x) => x.date);
  const safetyLogsPrevMonth = countInPrevMonth(safetyLogs, (x) => x.date);
  const safetyLogsMoM = pctChangeMoM(safetyLogsThisMonth, safetyLogsPrevMonth);

  // Occurrences (active = not CLOSED)
  const allOccurrences = Array.isArray(occurrences) ? occurrences : [];
  const activeOccurrences = allOccurrences.filter((o) => o.status !== 'CLOSED');
  const occThisMonth = countInMonth(allOccurrences, (x) => x.date);
  const occPrevMonth = countInPrevMonth(allOccurrences, (x) => x.date);
  const occMoM = pctChangeMoM(occThisMonth, occPrevMonth);

  // Articles (use post_date or fallbacks)
  const getArticleDate = (a) => a.post_date || a.postDate || a.date || a.createdAt || a.updatedAt;
  const artThisMonth = countInMonth(articles, getArticleDate);
  const artPrevMonth = countInPrevMonth(articles, getArticleDate);
  const artMoM = pctChangeMoM(artThisMonth, artPrevMonth);

  // Users (best-effort created fields)
  const users = usersResult?.users || [];
  const getUserCreated = (u) => (
    u.createdAt
    || u.created_at
    || u.dateCreated
    || u.createdOn
    || u.registeredAt
    || u.updatedAt
  );
  const usersThisMonth = countInMonth(users, getUserCreated);
  const usersPrevMonth = countInPrevMonth(users, getUserCreated);
  const usersMoM = pctChangeMoM(usersThisMonth, usersPrevMonth);

  const stats = [
    {
      title: 'Safety Logs',
      value: String(Array.isArray(safetyLogs) ? safetyLogs.length : 0),
      change: safetyLogsMoM,
      icon: faShieldAlt,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-900/40',
    },
    {
      title: 'Active Occurrences',
      value: String(activeOccurrences.length),
      change: occMoM,
      icon: faPuzzlePiece,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-900/40',
    },
    {
      title: 'Published Articles',
      value: String(Array.isArray(articles) ? articles.length : 0),
      change: artMoM,
      icon: faBullhorn,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-900/40',
    },
    {
      title: 'Active Users',
      value: String(users.length),
      change: usersMoM,
      icon: faUsers,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-900/40',
    },
  ];

  const recentActivities = [
    {
      type: 'Safety Log',
      title: 'New safety incident reported',
      status: 'pending',
      icon: faClock,
    },
    {
      type: 'Occurrence',
      title: 'Risk assessment completed',
      status: 'completed',
      icon: faCheckCircle,
    },
    {
      type: 'Article',
      title: 'Safety guidelines updated',
      status: 'completed',
      icon: faCheckCircle,
    },
    {
      type: 'Alert',
      title: 'High priority occurrence detected',
      status: 'warning',
      icon: faExclamationTriangle,
    },
  ];

  const quickActions = [
    {
      title: 'Create Safety Log', icon: faShieldAlt, color: 'bg-blue-700 hover:bg-blue-800', to: '/private/safety-logs/all',
    },
    {
      title: 'Report Occurrence', icon: faPuzzlePiece, color: 'bg-orange-700 hover:bg-orange-800', to: '/private/occurrences/all',
    },
    {
      title: 'Publish Article', icon: faBullhorn, color: 'bg-green-700 hover:bg-green-800', to: '/private/articles',
    },
    {
      title: 'Manage Users', icon: faUsers, color: 'bg-purple-700 hover:bg-purple-800', to: '/private/users',
    },
  ];

  const getStatusColor = (status) => {
    if (status === 'completed') return 'bg-green-900/40';
    if (status === 'warning') return 'bg-red-900/40';
    return 'bg-yellow-900/40';
  };

  const getStatusTextColor = (status) => {
    if (status === 'completed') return 'text-green-300';
    if (status === 'warning') return 'text-red-300';
    return 'text-yellow-300';
  };

  const getStatusBadgeColor = (status) => {
    if (status === 'completed') return 'bg-green-900/40 text-green-200';
    if (status === 'warning') return 'bg-red-900/40 text-red-200';
    return 'bg-yellow-900/40 text-yellow-200';
  };

  return (
    <div className="p-6 space-y-6 dark:bg-gray-900/50 min-h-screen">
      {/* Welcome Header */}
      <div className="bg-private-clr rounded-xl p-6 text-white shadow-xl hover:shadow-2xl transition-shadow duration-300">
        <div>
          <h1 className="text-3xl font-bold mb-2">Welcome back!</h1>
          <p className="text-gray-300">Here&apos;s what&apos;s happening with your SMS system today.</p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.title} className="bg-private-clr rounded-xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-300 mb-1">{stat.title}</p>
                <p className="text-3xl font-bold text-white">{stat.value}</p>
                <p className={`text-sm font-medium ${stat.change.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
                  {stat.change}
                  {' '}
                  from last month
                </p>
              </div>
              <div className={`p-3 rounded-full ${stat.bgColor}`}>
                <FontAwesomeIcon
                  icon={stat.icon}
                  className={`text-2xl bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <div className="bg-private-clr rounded-xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
            <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
            <div className="space-y-3">
              {quickActions.map((action) => (
                <Link
                  key={action.title}
                  to={action.to}
                  className={`w-full ${action.color} text-white p-4 rounded-lg flex items-center justify-between transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl`}
                >
                  <span className="font-medium">{action.title}</span>
                  <FontAwesomeIcon icon={action.icon} className="text-lg" />
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="lg:col-span-2">
          <div className="bg-private-clr rounded-xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Recent Activities</h2>
              <button type="button" className="text-private-text-link hover:text-blue-400 font-medium transition-colors">
                View All
              </button>
            </div>
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.title} className="flex items-center space-x-4 p-4 rounded-lg bg-private hover:bg-private-clr transition-all duration-200 shadow-md hover:shadow-lg">
                  <div className={`p-2 rounded-full ${getStatusColor(activity.status)}`}>
                    <FontAwesomeIcon
                      icon={activity.icon}
                      className={`text-lg ${getStatusTextColor(activity.status)}`}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{activity.title}</p>
                    <p className="text-sm text-gray-300">{activity.type}</p>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(activity.status)}`}>
                    {activity.status}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* System Health */}
      <div className="bg-private-clr rounded-xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
        <h2 className="text-xl font-bold text-white mb-4">System Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-900/40 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
              <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-green-300" />
            </div>
            <h3 className="font-semibold text-white">System Status</h3>
            <p className="text-green-300 font-medium">All Systems Operational</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-900/40 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
              <FontAwesomeIcon icon={faChartLine} className="text-2xl text-blue-300" />
            </div>
            <h3 className="font-semibold text-white">Performance</h3>
            <p className="text-blue-300 font-medium">98.5% Uptime</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-900/40 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
              <FontAwesomeIcon icon={faShieldAlt} className="text-2xl text-purple-300" />
            </div>
            <h3 className="font-semibold text-white">Security</h3>
            <p className="text-purple-300 font-medium">All Clear</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
